import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import "./HomePage.css"

const slides = [
  {
    label: "Pharmacy First - NHS Service",
    description: "Get NHS treatments for common conditions. Skip the GP wait.",
    link: "/pharmacy-first",
    linkText: "Learn More",
    color: "#16a34a",
    image: "/images/slides/pharmacy-first.jpg"
  },
  {
    label: "Weight Loss Service",
    description: "Professional support for your weight management journey with personalised plans.",
    link: "/weight-loss",
    linkText: "Learn More",
    color: "#2563eb",
    image: "/images/slides/weight-loss.jpg"
  },
  {
    label: "New Medicine Service",
    description: "Support and guidance when starting a new medication to ensure it works well for you.",
    link: "/new-medicine-clinic",
    linkText: "Learn More",
    color: "#0d9488",
    image: "/images/slides/new-medicine-clinic.jpg"
  },
  {
    label: "Flu Vaccination",
    description: "Protect yourself and your loved ones this flu season.",
    link: "/flu-vaccination",
    linkText: "Learn More",
    color: "#7c3aed",
    image: "/images/slides/flu-vaccination.jpg"
  },
]

function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0)

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }, [])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }, [])

  useEffect(() => {
    const timer = setInterval(nextSlide, 10000)
    return () => clearInterval(timer)
  }, [nextSlide])

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-content">
          <h1>Goosnargh Pharmacy</h1>
          <p>
            Your trusted local pharmacy providing quality healthcare services and products to the Goosnargh community.
          </p>
          <div className="hero-buttons">
            <a href="#services" className="button primary">
              Our Services
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </a>
            <a href="#contact" className="button outline">
              Contact Us
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <section className="slideshow-section">
        <div className="slideshow-container">
          <button className="slideshow-arrow slideshow-arrow-left" onClick={prevSlide} aria-label="Previous slide">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="slideshow-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {slides.map((slide, index) => (
              <div
                key={index}
                className="slideshow-slide"
                style={{
                  backgroundImage: `url(${slide.image})`,
                  backgroundColor: slide.color
                }}
              >
                <div className="slideshow-overlay" style={{ backgroundColor: slide.color }} />
                <div className="slideshow-content">
                  <span className="slideshow-label">{slide.label}</span>
                  <p className="slideshow-description">{slide.description}</p>
                  <Link to={slide.link} className="slideshow-link">
                    {slide.linkText}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <button className="slideshow-arrow slideshow-arrow-right" onClick={nextSlide} aria-label="Next slide">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
        <div className="slideshow-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`slideshow-dot ${index === currentSlide ? "active" : ""}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      <section id="services" className="services-section">
        <div className="section-header">
          <h2>Our Services</h2>
          <p>We offer a wide range of pharmacy services to meet your healthcare needs.</p>
        </div>

        <div className="services-grid">
          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </div>
            <h3>Pharmacy First</h3>
            <p>Get NHS treatments for common conditions like earache, sore throat, UTIs, and more. Skip the GP wait.</p>
            <Link to="/pharmacy-first" className="service-link">
              Learn more
            </Link>
          </div>

          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2a10 10 0 1 0 10 10H12V2Z" />
                <path d="M12 12 2.1 9.3" />
                <path d="m12 12 3.5 8.6" />
              </svg>
            </div>
            <h3>Weight Loss Service</h3>
            <p>Professional support for your weight management journey with personalised plans.</p>
            <Link to="/weight-loss" className="service-link">
              Learn more
            </Link>
          </div>

          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.5 20H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H20a2 2 0 0 1 2 2v3" />
                <circle cx="18" cy="18" r="3" />
                <path d="M18 14v1" />
                <path d="M18 21v1" />
                <path d="M22 18h-1" />
                <path d="M15 18h-1" />
              </svg>
            </div>
            <h3>New Medicine Service</h3>
            <p>Support and guidance when starting a new medication to ensure it works well for you.</p>
            <Link to="/new-medicine-clinic" className="service-link">
              Learn more
            </Link>
          </div>

          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
              </svg>
            </div>
            <h3>Contraception</h3>
            <p>Confidential contraception advice and services from our trained pharmacists.</p>
            <Link to="/contraception" className="service-link">
              Learn more
            </Link>
          </div>

          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m18 2 4 4" />
                <path d="m17 7 3-3" />
                <path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5" />
                <path d="m9 11 4 4" />
                <path d="m5 19-3 3" />
                <path d="m14 4 6 6" />
              </svg>
            </div>
            <h3>Flu Vaccination</h3>
            <p>Protect yourself and your loved ones this flu season.</p>
            <Link to="/flu-vaccination" className="service-link">
              Learn more
            </Link>
          </div>

          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="4" r="2" />
                <circle cx="18" cy="8" r="2" />
                <circle cx="20" cy="16" r="2" />
                <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
              </svg>
            </div>
            <h3>Veterinary Prescriptions</h3>
            <p>Competitive prices on medications for your pets and animals.</p>
            <Link to="/private-prescriptions" className="service-link">
              Learn more
            </Link>
          </div>

          <div className="service-card service-card-featured">
            <div className="service-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" />
              </svg>
            </div>
            <h3>Blood Pressure Checks</h3>
            <p>Blood pressure monitoring and hypertension screening to help manage your cardiovascular health.</p>
            <Link to="/hypertension" className="service-link">
              Learn more
            </Link>
          </div>

        </div>
      </section>

      <section id="about" className="about-section">
        <div className="about-content">
          <h2>About Goosnargh Pharmacy</h2>
          <p>
            Goosnargh Pharmacy has been serving the local community for over 20 years. Our team of experienced
            pharmacists and healthcare professionals are dedicated to providing personalized care and expert advice.
          </p>
          <p>
            We pride ourselves on our friendly service and commitment to the health and wellbeing of our customers. Our
            modern pharmacy offers a comprehensive range of services and products to meet all your healthcare needs.
          </p>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <div className="section-header">
          <h2>Contact Us</h2>
          <p>We're here to help. Get in touch with our friendly team.</p>
        </div>

        <div className="contact-grid">
          <div className="contact-card">
            <div className="contact-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <h3>Visit Us</h3>
            <p>
              859 Whittingham Ln
              <br />
              Preston
              <br />
              PR3 2AU
            </p>
          </div>

          <div className="contact-card">
            <div className="contact-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h3>Opening Hours</h3>
            <p>
              Monday - Thursday: 9am - 6pm
              <br />
              Friday: 9am - 1pm, 2pm - 6pm
              <br />
              Saturday - Sunday: Closed
            </p>
          </div>

          <div className="contact-card">
            <div className="contact-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <h3>Contact Details</h3>
            <p>
              Phone: 01772 865238
              <br />
              Email: goosnarghpharmacy859@hotmail.com
            </p>
          </div>
        </div>

        <div className="map-container">
          <iframe
            title="Goosnargh Pharmacy Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2338.8!2d-2.6867!3d53.8267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487b14a1a1a1a1a1%3A0x0!2s859+Whittingham+Ln%2C+Preston+PR3+2AU!5e0!3m2!1sen!2suk!4v1700000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  )
}

export default HomePage
